import { useState, useRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Music2, TrendingUp, FileText, Upload, CheckCircle2, AlertCircle, Download, X, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Header } from "../components/Header";
import { toast } from "sonner";
import axios from "axios";

const API = `${window.location.origin}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } }
};

const GENRE_OPTIONS = [
  "House", "Tech House", "Deep House", "Minimal / Deep Tech",
  "UK Garage", "Speed Garage", "Bassline", "Drum & Bass", "Techno", "Other"
];

const PLATFORM_OPTIONS = [
  "Spotify", "Apple Music", "YouTube", "SoundCloud", "Amazon Music", "Other"
];

// Hero Section
const HeroSection = () => {
  const scrollToForm = () => {
    document.getElementById('publishing-form')?.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToExplainer = () => {
    document.getElementById('catalogue-deals')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1633319987325-aae32f3d8dd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxkYXJrJTIwY29uY3JldGUlMjB0ZXh0dXJlJTIwd2FsbHxlbnwwfHx8fDE3NzE0MTI0MDV8MA&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="publishing-hero"
    >
      <div className="absolute inset-0 bg-black/85" />
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-left">
        <motion.h1
          {...fadeUp}
          className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tighter leading-none text-white mb-8"
        >
          Your Music Has Value.<br />
          <span className="text-neon">Let's Maximise It.</span>
        </motion.h1>
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.15 }}
          className="max-w-2xl mb-12"
        >
          <p className="text-lg md:text-xl text-zinc-400 leading-relaxed">
            Explore publishing opportunities for your existing catalogue and new music. 
            TestPressClub Publishing helps artists, producers and songwriters understand the 
            potential value of their music and connect with suitable publishing opportunities.
          </p>
        </motion.div>
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Button
            onClick={scrollToForm}
            className="bg-neon text-black hover:bg-white hover:text-black rounded-none uppercase font-bold tracking-wider px-10 py-6 text-lg transition-colors duration-300 border border-neon hover:border-white"
            data-testid="hero-submit-btn"
          >
            Submit Your Catalogue
          </Button>
          <Button
            onClick={scrollToExplainer}
            className="bg-transparent text-white border border-zinc-600 hover:border-white rounded-none uppercase font-bold tracking-wider px-10 py-6 text-lg transition-colors duration-300"
            data-testid="hero-explore-btn"
          >
            Explore Publishing
          </Button>
        </motion.div>
      </div>
      <motion.div
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-px h-16 bg-zinc-700" />
      </motion.div>
    </section>
  );
};

// Catalogue Deals Section
const CatalogueDealsSection = () => {
  const scrollToForm = () => {
    document.getElementById('publishing-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="catalogue-deals" className="py-24 md:py-32 bg-[#09090b] border-t border-zinc-800" data-testid="catalogue-deals-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-5 h-5 text-neon" />
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon">Existing Catalogue</p>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
              Your Catalogue Could Be Worth More Than You Think.
            </h2>
            <p className="text-zinc-400 text-lg leading-relaxed mb-6">
              Have released music that's generating streams? We can assess your catalogue's 
              performance and explore potential publishing deals through our industry contacts.
            </p>
            <p className="text-zinc-500 leading-relaxed mb-10">
              Depending on your catalogue, rights ownership and the opportunities available, 
              this may include exploring an upfront payment in exchange for agreed publishing 
              rights or another suitable publishing arrangement.
            </p>
            <Button
              onClick={scrollToForm}
              className="bg-neon text-black hover:bg-white hover:text-black rounded-none uppercase font-bold tracking-wider px-8 py-5 text-base transition-colors duration-300 border border-neon hover:border-white"
              data-testid="catalogue-cta-btn"
            >
              Assess My Catalogue
            </Button>
          </motion.div>
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="border border-zinc-800 p-8 bg-zinc-900/30"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-6">What we look at</p>
            <div className="space-y-4">
              {["Total catalogue streams and growth trajectory", "Revenue and streaming performance over time", "Rights ownership and existing agreements", "Genre positioning and market demand"].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-neon rounded-full mt-2 flex-shrink-0" />
                  <p className="text-zinc-400">{item}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// New Music Section
const NewMusicSection = () => {
  const scrollToForm = () => {
    document.getElementById('publishing-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1672938662121-9182d3060dd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHx2aW55bCUyMHJlY29yZCUyMHR1cm50YWJsZSUyMGNsb3NlJTIwdXAlMjBkYXJrfGVufDB8fHx8MTc3MTQxMjQwNnww&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
      data-testid="new-music-section"
    >
      <div className="absolute inset-0 bg-black/90" />
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="max-w-3xl">
          <motion.div {...fadeUp}>
            <div className="flex items-center gap-3 mb-6">
              <Music2 className="w-5 h-5 text-neon" />
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon">New Music</p>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
              Building Something Bigger?
            </h2>
            <p className="text-zinc-400 text-lg leading-relaxed mb-6">
              For active artists, producers and songwriters, we can explore publishing 
              opportunities for new music and help identify ways to maximise the value 
              of your compositions as your catalogue grows.
            </p>
            <p className="text-zinc-500 leading-relaxed mb-10">
              We're interested in ambitious creators making music with long-term potential.
            </p>
            <Button
              onClick={scrollToForm}
              className="bg-transparent text-neon border border-neon hover:bg-neon hover:text-black rounded-none uppercase font-bold tracking-wider px-8 py-5 text-base transition-colors duration-300"
              data-testid="new-music-cta-btn"
            >
              Submit Your Details
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Tell Us About Your Music",
      description: "Submit your artist details, genres, catalogue size and publishing situation."
    },
    {
      number: "02",
      title: "Share Your Streaming Data",
      description: "Upload a CSV export covering the last six months so we can assess your catalogue's performance."
    },
    {
      number: "03",
      title: "Explore the Opportunity",
      description: "We'll review your submission and contact you if there is a potential publishing opportunity worth discussing."
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-[#09090b] border-t border-zinc-800" data-testid="publishing-how-it-works">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Process</p>
          <h2 className="font-heading text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-16">
            How It Works
          </h2>
        </motion.div>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          {steps.map((step) => (
            <motion.div key={step.number} variants={fadeUp} className="group">
              <div className="border border-zinc-800 p-8 h-full hover:border-neon/50 transition-colors duration-300">
                <span className="text-6xl font-heading font-bold text-zinc-800 group-hover:text-neon/30 transition-colors duration-300">
                  {step.number}
                </span>
                <h3 className="font-heading text-2xl md:text-3xl font-medium uppercase tracking-wide text-white mt-4 mb-4">
                  {step.title}
                </h3>
                <p className="text-zinc-500 leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Multi-select dropdown component
const MultiSelect = ({ options, selected, onChange, placeholder, testId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const toggle = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between bg-transparent border-2 border-zinc-800 focus:border-neon px-4 py-4 text-left transition-colors duration-200"
        data-testid={testId}
      >
        <span className={selected.length > 0 ? "text-white" : "text-zinc-600"}>
          {selected.length > 0 ? selected.join(", ") : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="absolute z-20 w-full mt-1 bg-zinc-900 border border-zinc-700 max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => toggle(option)}
              className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                selected.includes(option)
                  ? "bg-neon/10 text-neon"
                  : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      )}
      {isOpen && <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />}
    </div>
  );
};

// Submission Form Section
const SubmissionFormSection = () => {
  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    artist_name: "",
    email: "",
    country: "",
    main_genres: [],
    secondary_genres: "",
    spotify_url: "",
    soundcloud_url: "",
    other_links: "",
    released_tracks: "",
    total_streams: "",
    streams_last_six_months: "",
    streaming_platforms: [],
    actively_releasing: "",
    has_publishing_deal: "",
    owns_publishing_rights: "",
    additional_info: "",
    no_csv_available: false,
    consent_privacy: false,
    consent_marketing: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error("Only CSV files are accepted");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10 MB");
      return;
    }
    setCsvFile(file);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.consent_privacy) {
      toast.error("Please confirm you've read the privacy notice");
      return;
    }
    if (formData.main_genres.length === 0) {
      toast.error("Please select at least one main genre");
      return;
    }
    if (formData.streaming_platforms.length === 0) {
      toast.error("Please select at least one streaming platform");
      return;
    }
    if (!formData.actively_releasing) {
      toast.error("Please indicate if you are actively releasing music");
      return;
    }
    if (!formData.has_publishing_deal) {
      toast.error("Please indicate your publishing deal status");
      return;
    }
    if (!formData.owns_publishing_rights) {
      toast.error("Please indicate your publishing rights status");
      return;
    }
    if (!csvFile && !formData.no_csv_available) {
      toast.error("Please upload a CSV file or check the box if you cannot provide one");
      return;
    }

    setIsSubmitting(true);

    try {
      const fd = new FormData();
      fd.append("full_name", formData.full_name);
      fd.append("artist_name", formData.artist_name);
      fd.append("email", formData.email);
      if (formData.country) fd.append("country", formData.country);
      fd.append("main_genres", formData.main_genres.join(","));
      if (formData.secondary_genres) fd.append("secondary_genres", formData.secondary_genres);
      if (formData.spotify_url) fd.append("spotify_url", formData.spotify_url);
      if (formData.soundcloud_url) fd.append("soundcloud_url", formData.soundcloud_url);
      if (formData.other_links) fd.append("other_links", formData.other_links);
      fd.append("released_tracks", formData.released_tracks);
      fd.append("total_streams", formData.total_streams);
      fd.append("streams_last_six_months", formData.streams_last_six_months);
      fd.append("streaming_platforms", formData.streaming_platforms.join(","));
      fd.append("actively_releasing", formData.actively_releasing);
      fd.append("has_publishing_deal", formData.has_publishing_deal);
      fd.append("owns_publishing_rights", formData.owns_publishing_rights);
      if (formData.additional_info) fd.append("additional_info", formData.additional_info);
      fd.append("no_csv_available", formData.no_csv_available);
      fd.append("consent_privacy", formData.consent_privacy);
      fd.append("consent_marketing", formData.consent_marketing);
      if (csvFile) fd.append("csv_file", csvFile);

      await axios.post(`${API}/publishing/submissions`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setIsSubmitted(true);
      toast.success("Catalogue submitted successfully!");
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="publishing-form" className="py-24 md:py-32 bg-[#09090b] border-t border-zinc-800" data-testid="publishing-form-section">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <CheckCircle2 className="w-16 h-16 text-neon mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-6">
              Thank You — Your Catalogue Has Been Submitted.
            </h2>
            <p className="text-zinc-400 text-lg leading-relaxed">
              We've received your details and streaming data. We'll review your submission 
              and contact you if we identify a potential publishing opportunity worth discussing.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="publishing-form" className="py-24 md:py-32 bg-[#09090b] border-t border-zinc-800" data-testid="publishing-form-section">
      <div className="max-w-2xl mx-auto px-6">
        <motion.div {...fadeUp}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Submission</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4">
            Catalogue Submission
          </h2>
          <p className="text-zinc-500 mb-12">Fields marked with * are required.</p>
        </motion.div>

        <motion.form
          ref={formRef}
          onSubmit={handleSubmit}
          className="space-y-12"
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
        >
          {/* Contact Details */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon mb-6">Contact Details</p>
            <div className="space-y-6">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Full Name *</label>
                <Input type="text" name="full_name" value={formData.full_name} onChange={handleChange} required
                  placeholder="Your full name"
                  className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                  data-testid="pub-full-name" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Artist / Producer Name *</label>
                <Input type="text" name="artist_name" value={formData.artist_name} onChange={handleChange} required
                  placeholder="Your artist or producer name"
                  className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                  data-testid="pub-artist-name" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Email Address *</label>
                <Input type="email" name="email" value={formData.email} onChange={handleChange} required
                  placeholder="your@email.com"
                  className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                  data-testid="pub-email" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Country of Residence</label>
                <Input type="text" name="country" value={formData.country} onChange={handleChange}
                  placeholder="e.g. United Kingdom"
                  className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                  data-testid="pub-country" />
              </div>
            </div>
          </div>

          {/* Music Profile */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon mb-6">Music Profile</p>
            <div className="space-y-6">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Main Genre *</label>
                <MultiSelect
                  options={GENRE_OPTIONS}
                  selected={formData.main_genres}
                  onChange={(v) => setFormData(prev => ({ ...prev, main_genres: v }))}
                  placeholder="Select your main genres"
                  testId="pub-main-genres"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Secondary Genres</label>
                <Input type="text" name="secondary_genres" value={formData.secondary_genres} onChange={handleChange}
                  placeholder="Any other genres you produce"
                  className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                  data-testid="pub-secondary-genres" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Spotify Artist Profile URL</label>
                <Input type="url" name="spotify_url" value={formData.spotify_url} onChange={handleChange}
                  placeholder="https://open.spotify.com/artist/..."
                  className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                  data-testid="pub-spotify-url" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">SoundCloud Profile URL</label>
                <Input type="url" name="soundcloud_url" value={formData.soundcloud_url} onChange={handleChange}
                  placeholder="https://soundcloud.com/..."
                  className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                  data-testid="pub-soundcloud-url" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Other Relevant Links</label>
                <Input type="text" name="other_links" value={formData.other_links} onChange={handleChange}
                  placeholder="YouTube, Bandcamp, etc."
                  className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                  data-testid="pub-other-links" />
              </div>
            </div>
          </div>

          {/* Catalogue Details */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon mb-6">Catalogue Details</p>
            <div className="space-y-6">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Approximate Number of Released Tracks *</label>
                <Input type="number" name="released_tracks" value={formData.released_tracks} onChange={handleChange} required min="0"
                  placeholder="e.g. 25"
                  className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                  data-testid="pub-released-tracks" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Approximate Total Catalogue Streams *</label>
                <Input type="number" name="total_streams" value={formData.total_streams} onChange={handleChange} required min="0"
                  placeholder="e.g. 5000000"
                  className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                  data-testid="pub-total-streams" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Approximate Streams Over the Last Six Months *</label>
                <Input type="number" name="streams_last_six_months" value={formData.streams_last_six_months} onChange={handleChange} required min="0"
                  placeholder="e.g. 1200000"
                  className="bg-transparent border-0 border-b-2 border-zinc-800 focus:border-neon rounded-none px-0 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white"
                  data-testid="pub-streams-6mo" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Main Streaming Platforms *</label>
                <MultiSelect
                  options={PLATFORM_OPTIONS}
                  selected={formData.streaming_platforms}
                  onChange={(v) => setFormData(prev => ({ ...prev, streaming_platforms: v }))}
                  placeholder="Select your platforms"
                  testId="pub-platforms"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Are You Actively Releasing New Music? *</label>
                <div className="flex gap-6">
                  {["Yes", "No"].map(opt => (
                    <button key={opt} type="button"
                      onClick={() => setFormData(prev => ({ ...prev, actively_releasing: opt }))}
                      className={`px-6 py-3 border text-sm uppercase tracking-wider font-bold transition-colors ${
                        formData.actively_releasing === opt
                          ? "border-neon text-neon bg-neon/10"
                          : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                      }`}
                      data-testid={`pub-releasing-${opt.toLowerCase()}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Do You Currently Have a Publishing Deal? *</label>
                <div className="flex gap-4 flex-wrap">
                  {["Yes", "No", "Unsure"].map(opt => (
                    <button key={opt} type="button"
                      onClick={() => setFormData(prev => ({ ...prev, has_publishing_deal: opt }))}
                      className={`px-6 py-3 border text-sm uppercase tracking-wider font-bold transition-colors ${
                        formData.has_publishing_deal === opt
                          ? "border-neon text-neon bg-neon/10"
                          : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                      }`}
                      data-testid={`pub-deal-${opt.toLowerCase()}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Do You Own or Control the Relevant Publishing Rights? *</label>
                <div className="flex gap-4 flex-wrap">
                  {["Yes", "Partially", "No", "Unsure"].map(opt => (
                    <button key={opt} type="button"
                      onClick={() => setFormData(prev => ({ ...prev, owns_publishing_rights: opt }))}
                      className={`px-6 py-3 border text-sm uppercase tracking-wider font-bold transition-colors ${
                        formData.owns_publishing_rights === opt
                          ? "border-neon text-neon bg-neon/10"
                          : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                      }`}
                      data-testid={`pub-rights-${opt.toLowerCase()}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">Additional Information</label>
                <Textarea name="additional_info" value={formData.additional_info} onChange={handleChange}
                  placeholder="Anything else you'd like us to know about your music or publishing situation..."
                  rows={4}
                  className="bg-transparent border-2 border-zinc-800 focus:border-neon rounded-none px-4 py-4 text-lg placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:ring-offset-0 w-full text-white resize-none"
                  data-testid="pub-additional-info" />
              </div>
            </div>
          </div>

          {/* CSV Upload */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-neon mb-6">Streaming Data</p>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-3">
              Upload Your Last Six Months of Streaming Data
            </label>
            <p className="text-zinc-600 text-sm mb-4">
              Upload a CSV export showing your recent streaming performance. If possible, include 
              track titles, artist names, streaming platforms, reporting dates and stream counts.
            </p>

            {!formData.no_csv_available && (
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed p-8 text-center transition-colors duration-200 mb-4 ${
                  dragActive ? "border-neon bg-neon/5" : csvFile ? "border-neon/50 bg-neon/5" : "border-zinc-800 hover:border-zinc-600"
                }`}
                data-testid="pub-csv-dropzone"
              >
                {csvFile ? (
                  <div className="flex items-center justify-center gap-4">
                    <FileText className="w-6 h-6 text-neon" />
                    <span className="text-white">{csvFile.name}</span>
                    <span className="text-zinc-500 text-sm">({(csvFile.size / 1024).toFixed(1)} KB)</span>
                    <button type="button" onClick={() => { setCsvFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                      className="text-zinc-500 hover:text-red-400 transition-colors"
                      data-testid="pub-csv-remove"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-500 mb-2">Drag & drop your CSV file here</p>
                    <p className="text-zinc-600 text-sm mb-4">or</p>
                    <Button type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-transparent text-zinc-400 border border-zinc-700 hover:border-zinc-500 hover:text-white rounded-none px-6 py-2 transition-colors"
                      data-testid="pub-csv-browse"
                    >
                      Browse Files
                    </Button>
                    <p className="text-zinc-700 text-xs mt-3">CSV only, max 10 MB</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                  data-testid="pub-csv-input"
                />
              </div>
            )}

            <div className="flex items-center gap-3 mb-4">
              <a href={`${API}/publishing/csv-template`} download
                className="text-neon text-sm hover:text-white underline transition-colors flex items-center gap-2"
                data-testid="pub-csv-template-link"
              >
                <Download className="w-4 h-4" />
                Download blank CSV template
              </a>
            </div>

            <div className="flex items-start gap-4">
              <Checkbox
                id="no_csv_available"
                checked={formData.no_csv_available}
                onCheckedChange={(checked) => {
                  setFormData(prev => ({ ...prev, no_csv_available: checked }));
                  if (checked) setCsvFile(null);
                }}
                className="mt-1 border-zinc-600 data-[state=checked]:bg-neon data-[state=checked]:border-neon rounded-none"
                data-testid="pub-no-csv-checkbox"
              />
              <label htmlFor="no_csv_available" className="text-zinc-500 text-sm cursor-pointer">
                I don't have access to a CSV export right now. I've provided approximate streaming figures above instead.
              </label>
            </div>
          </div>

          {/* Consent */}
          <div className="space-y-4 pt-4">
            <div className="flex items-start gap-4">
              <Checkbox
                id="consent_privacy"
                checked={formData.consent_privacy}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent_privacy: checked }))}
                className="mt-1 border-zinc-600 data-[state=checked]:bg-neon data-[state=checked]:border-neon rounded-none"
                data-testid="pub-consent-privacy"
              />
              <label htmlFor="consent_privacy" className="text-zinc-400 text-sm leading-relaxed cursor-pointer">
                I have read the privacy notice and agree to my submitted information being used 
                to assess potential publishing opportunities. *
                {" "}
                <Link to="/terms" className="text-neon hover:text-white underline transition-colors">
                  View Terms & Conditions
                </Link>
              </label>
            </div>
            <div className="flex items-start gap-4">
              <Checkbox
                id="consent_marketing"
                checked={formData.consent_marketing}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent_marketing: checked }))}
                className="mt-1 border-zinc-600 data-[state=checked]:bg-neon data-[state=checked]:border-neon rounded-none"
                data-testid="pub-consent-marketing"
              />
              <label htmlFor="consent_marketing" className="text-zinc-500 text-sm leading-relaxed cursor-pointer">
                I agree to be contacted by TestPressClub about publishing opportunities and related services.
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="pt-4">
            <Button type="submit" disabled={isSubmitting}
              className="w-full bg-neon text-black hover:bg-white hover:text-black rounded-none uppercase font-bold tracking-wider px-10 py-6 text-lg transition-colors duration-300 border border-neon hover:border-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="pub-submit-btn"
            >
              {isSubmitting ? "Submitting..." : "Submit Your Catalogue"}
            </Button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  const scrollToForm = () => {
    document.getElementById('publishing-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1665623328945-35acef28302a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHx1bmRlcmdyb3VuZCUyMHRlY2hubyUyMGNsdWIlMjBjcm93ZCUyMGRhcmslMjBzaWxob3VldHRlfGVufDB8fHx8MTc3MTQxMjQxOXww&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
      data-testid="publishing-final-cta"
    >
      <div className="absolute inset-0 bg-black/90" />
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <motion.div {...fadeUp}>
          <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
            Your Next Opportunity Could Already Be In Your Catalogue.
          </h2>
          <p className="text-zinc-400 text-lg mb-10">
            Tell us what you've built. Let's explore what's possible.
          </p>
          <Button
            onClick={scrollToForm}
            className="bg-neon text-black hover:bg-white hover:text-black rounded-none uppercase font-bold tracking-wider px-10 py-6 text-lg transition-colors duration-300 border border-neon hover:border-white"
            data-testid="final-cta-submit-btn"
          >
            Submit Your Catalogue
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="py-12 bg-[#09090b] border-t border-zinc-800" data-testid="publishing-footer">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_vinyl-scouts/artifacts/dfa6la5v_TPRESSLOGOSQ.png"
            alt="Test Press Club"
            className="h-8 w-auto"
          />
          <span className="text-zinc-600 text-sm">© 2026</span>
        </div>
        <div className="flex items-center gap-4 text-zinc-600 text-sm">
          <Link to="/" className="hover:text-neon transition-colors duration-300">Home</Link>
          <span className="text-zinc-700">|</span>
          <Link to="/dj-list" className="hover:text-neon transition-colors duration-300">Join the DJ List</Link>
          <span className="text-zinc-700">|</span>
          <Link to="/terms" className="hover:text-neon transition-colors duration-300">Terms & Conditions</Link>
        </div>
      </div>
    </div>
  </footer>
);

// Main Page
export default function PublishingPage() {
  useEffect(() => {
    document.title = "Music Publishing Opportunities for Artists & Producers | TestPressClub";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "Explore music publishing opportunities for existing catalogues and new releases. Submit your streaming data to TestPressClub Publishing for review.");
  }, []);

  return (
    <div className="bg-[#09090b] min-h-screen">
      <div className="noise-overlay" />
      <Header />
      <HeroSection />
      <CatalogueDealsSection />
      <NewMusicSection />
      <HowItWorksSection />
      <SubmissionFormSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
}
